// Dashboard.tsx — the control center (Aurora's media-pipeline pattern): sections with status badges +
// per-item action buttons. This is the MAIN screen; the chat is a slide-out assistant that drives it.
// Sections: Accounts · Needs review (pending) · Approved (post/schedule) · Scheduled · Recent.
import { useCallback, useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../supabase';
import { getQueue, runDraft, approve, reject, recycle, editItem, postItem, schedule, getPrefs, setAutopilot, getConnections, refreshStats, coachMe, listMedia, attachMedia, detachMedia, postWithMedia, pollPostResult, WIRED_PLATFORMS, MEDIA_PLATFORMS, type QueueItem, type MediaFile } from '../autopilot';

const eng = (it: QueueItem) => (it.stats ? it.stats.likes + it.stats.reposts + it.stats.replies : 0);
function StageBar({ status }: { status: QueueItem['status'] }) {
  // the content journey: draft → review → approved → scheduled/posted
  const step = status === 'pending' ? 1 : status === 'approved' ? 2 : status === 'scheduled' ? 3 : status === 'posted' ? 4 : 0;
  if (step === 0) return null; // rejected → no journey
  return (
    <div className="stage" title={`stage ${step}/4`}>
      {[1, 2, 3, 4].map((s) => <i key={s} className={s <= step ? 'on' : ''} />)}
    </div>
  );
}
function Stats({ s }: { s: NonNullable<QueueItem['stats']> }) {
  return <span className="stats">♥ {s.likes} · ↻ {s.reposts} · 💬 {s.replies}</span>;
}

const COPY_PLATFORMS = ['linkedin', 'instagram', 'threads', 'reddit', 'youtube', 'tiktok', 'medium'];

function openAccount() {
  const url = 'https://orioralabs.com/settings?tab=connections';
  invoke('open_external', { url }).catch(() => { try { window.open(url, '_blank'); } catch { /* ignore */ } });
}

function relTime(iso?: string): string {
  if (!iso) return "";
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return "";
  const d = Math.floor(ms / 86400000), h = Math.floor(ms / 3600000), m = Math.floor(ms / 60000);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return "just now";
}

// The Pulse — the at-a-glance header + the insight-layer readout. Free: all computed from the queue the
// dashboard already loaded (no extra calls). Shows the operation's state + that the AI is learning their voice.
function Pulse({ items }: { items: QueueItem[] }) {
  const pending = items.filter((i) => i.status === "pending").length;
  const scheduled = items.filter((i) => i.status === "scheduled").length;
  const posted = items.filter((i) => i.status === "posted");
  const kept = items.filter((i) => i.status === "approved" || i.status === "posted" || i.status === "scheduled").length;
  const lastPosted = posted.map((i) => i.posted_at as string | undefined).filter(Boolean).sort().reverse()[0];
  const platCounts: Record<string, number> = {};
  posted.forEach((i) => { if (i.platform) platCounts[i.platform] = (platCounts[i.platform] ?? 0) + 1; });
  const topPlat = Object.entries(platCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
  const insight = kept > 0
    ? `Learning your voice from ${kept} kept post${kept > 1 ? "s" : ""}${topPlat ? ` · most active on ${topPlat}` : ""}.`
    : `Approve a few posts and I'll learn your voice — drafts sharpen each time.`;
  const top = posted.filter((i) => eng(i) > 0).sort((a, b) => eng(b) - eng(a))[0];
  const clip = (t: string, n: number) => (t.length > n ? `${t.slice(0, n)}…` : t);
  const [coaching, setCoaching] = useState('');
  const [coachBusy, setCoachBusy] = useState(false);
  async function runCoach() {
    setCoachBusy(true);
    try { const r = await coachMe(); setCoaching(r.coaching); } catch (e) { setCoaching(`Error: ${String(e)}`); }
    finally { setCoachBusy(false); }
  }
  return (
    <div className="pulse">
      <div className="pulse-stats">
        <span className="ps"><b>{pending}</b> to review</span>
        <span className="ps"><b>{scheduled}</b> scheduled</span>
        <span className="ps"><b>{posted.length}</b> posted</span>
        {lastPosted && <span className="ps dim">last {relTime(lastPosted)}</span>}
      </div>
      <div className="pulse-insight">✨ {insight}</div>
      {top && <div className="pulse-insight">🏆 Best post · {eng(top)} reactions — "{clip(String(top.draft), 60)}"</div>}
      <div className="pulse-coach">
        <button className="btn-sm" onClick={runCoach} disabled={coachBusy}>{coachBusy ? 'Analysing…' : '🎯 Coach me'}</button>
        {coaching && <div className="coach-output">{coaching}</div>}
      </div>
    </div>
  );
}

export default function Dashboard({ refreshKey }: { refreshKey?: number }) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [conns, setConns] = useState<Record<string, boolean>>({});
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [auto, setAuto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, p, c, mf] = await Promise.all([getQueue(), getPrefs(), getConnections(), listMedia().catch(() => [] as MediaFile[])]);
      setItems(q);
      setAuto(p?.autopilot === true);
      const map: Record<string, boolean> = {};
      c.forEach((x) => { if (x.connected) map[x.service] = true; });
      setConns(map);
      setMediaFiles(mf);
      setErr('');
    } catch (e) { setErr(String((e as Error).message)); } finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  // Phase-2 insight: pull live engagement onto posted items once on open, then re-read the queue.
  useEffect(() => { refreshStats().then(() => load()).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = (fn: () => Promise<unknown>) => async () => {
    setBusy(true); setErr('');
    try { await fn(); await load(); } catch (e) { setErr(String((e as Error).message)); } finally { setBusy(false); }
  };

  const mediaMap = Object.fromEntries(mediaFiles.map((f) => [f.path, f.download_url ?? '']));
  const pending = items.filter((i) => i.status === 'pending');
  const approved = items.filter((i) => i.status === 'approved');
  const dispatching = items.filter((i) => i.status === 'dispatching');
  const scheduled = items.filter((i) => i.status === 'scheduled');
  const recent = items.filter((i) => i.status === 'posted' || i.status === 'rejected').slice(0, 6);

  return (
    <div className="dash">
      <div className="dash-head">
        <h2>Your content</h2>
        <div className="row">
          <label className="toggle"><input type="checkbox" checked={auto} disabled={busy} onChange={(e) => act(() => setAutopilot(e.target.checked))()} /><span>Autopilot</span></label>
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={act(runDraft)}>{busy ? '…' : '+ Draft from a source'}</button>
        </div>
      </div>
      {err && <p className="error">{err}</p>}

      {loaded && <Pulse items={items} />}

      <div className="dash-grid">
        <div className="dash-main">
          <section className="dash-section">
            <h3>Needs review {pending.length > 0 && <span className="count">{pending.length}</span>}</h3>
            {loaded && pending.length === 0 && <p className="muted">Nothing to review — hit "+ Draft from a source", turn on Autopilot, or ask the assistant.</p>}
            {pending.map((it) => (
              <div key={it.id} className="ap-card">
                <div className="ap-meta">
                  <span className="badge pending">pending</span>
                  {it.platform && <span className="muted">{it.platform}</span>}
                  {it.recycled_from && <span className="muted">♻️ recycled</span>}
                  {it.suggested_scheduled_at && <span className="muted">⏰ suggested: {new Date(it.suggested_scheduled_at).toLocaleString()}</span>}
                  {it.source && <a className="muted src" href={it.source} target="_blank" rel="noreferrer">source ↗</a>}
                </div>
                <DraftBody it={it} busy={busy} act={act} />
                {Array.isArray(it.lint) && it.lint.length > 0 && (
                  <p className="muted" style={{ fontSize: 11, color: "#d97706", margin: "2px 0 6px" }}>⚠️ {it.lint.join(" · ")}</p>
                )}
                <StageBar status={it.status} />
                <div className="ap-actions">
                  <button className="btn btn-sm btn-primary" disabled={busy} onClick={act(() => approve(it.id))}>Approve ✓</button>
                  <button className="btn btn-sm" disabled={busy} onClick={act(() => reject(it.id))}>Reject</button>
                  <button className="btn btn-sm" onClick={() => navigator.clipboard.writeText(it.draft)}>Copy</button>
                </div>
              </div>
            ))}
          </section>

          {dispatching.length > 0 && (
            <section className="dash-section">
              <h3>Posting with media…</h3>
              {dispatching.map((it) => (
                <div key={it.id} className="ap-card">
                  <div className="ap-meta"><span className="badge approved">dispatching</span><span className="muted">{it.platform}</span></div>
                  <p className="ap-draft">{it.draft}</p>
                  <PostingPoller it={it} onDone={load} />
                </div>
              ))}
            </section>
          )}

          {approved.length > 0 && (
            <section className="dash-section">
              <h3>Approved — post or schedule</h3>
              {approved.map((it) => <ApprovedCard key={it.id} it={it} busy={busy} act={act} mediaMap={mediaMap} mediaFiles={mediaFiles} onReload={load} />)}
            </section>
          )}
        </div>

        <aside className="dash-rail">
          <section className="dash-section">
            <div className="dash-section-head"><h3>Accounts</h3><button className="link" onClick={openAccount}>Manage →</button></div>
            <div className="acct-strip">
              {WIRED_PLATFORMS.map((p) => <span key={p} className={`acct ${conns[p] ? 'on' : 'off'}`}><i className="dot" />{p}</span>)}
              {COPY_PLATFORMS.map((p) => <span key={p} className="acct copy"><i className="dot" />{p}</span>)}
            </div>
          </section>

          <WeekStrip items={items} />

          {scheduled.length > 0 && (
            <section className="dash-section">
              <h3>Scheduled</h3>
              {scheduled.map((it) => (
                <div key={it.id} className="ap-card">
                  <div className="ap-meta"><span className="badge approved">scheduled</span><span className="muted">{it.platform} · {it.scheduled_at ? new Date(it.scheduled_at).toLocaleString() : ''}</span></div>
                  <DraftBody it={it} busy={busy} act={act} />
                </div>
              ))}
            </section>
          )}

          {recent.length > 0 && (
            <section className="dash-section">
              <h3>Recent</h3>
              {recent.map((it) => (
                <div key={it.id} className="ap-card">
                  <div className="ap-meta">
                    <span className={`badge ${it.status}`}>{it.status}</span>
                    {it.stats && <Stats s={it.stats} />}
                    {it.post_url && <a className="muted src" href={it.post_url} target="_blank" rel="noreferrer">view ↗</a>}
                    {it.status === 'posted' && (
                      <button className="link" disabled={busy} title="Queue this post for another run — you review it before it goes out"
                        onClick={act(() => recycle(it.id))}>♻️ Recycle</button>
                    )}
                  </div>
                  <p className="ap-draft">{it.draft}</p>
                  <StageBar status={it.status} />
                </div>
              ))}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

// WEEK AHEAD — the posting plan at a glance (calendar view, competitor-gap close 2026-07-17):
// the next 7 days, each with what fires that day (scheduled) or is proposed for it (chat
// suggestions awaiting review, shown dimmed). Renders nothing when the week is empty.
function WeekStrip({ items }: { items: QueueItem[] }) {
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i); return d; });
  const timeOf = (it: QueueItem) =>
    it.status === 'scheduled' && it.scheduled_at ? Date.parse(it.scheduled_at)
      : it.status === 'pending' && it.suggested_scheduled_at ? Date.parse(it.suggested_scheduled_at) : NaN;
  const byDay = (d: Date) => {
    const from = d.getTime(), to = from + 86_400_000;
    return items.filter((it) => { const t = timeOf(it); return t >= from && t < to; }).sort((a, b) => timeOf(a) - timeOf(b));
  };
  if (!days.some((d) => byDay(d).length > 0)) return null;
  return (
    <section className="dash-section">
      <h3>Week ahead</h3>
      <div className="week-strip">
        {days.map((d, i) => {
          const dayItems = byDay(d);
          return (
            <div key={i} className={`week-day ${dayItems.length ? 'has' : ''}`}>
              <span className="wd-label">{i === 0 ? 'Today' : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}</span>
              <span className="wd-items">
                {dayItems.map((it) => (
                  <span key={it.id} className={`wd-item ${it.status === 'pending' ? 'proposed' : ''}`} title={it.draft}>
                    {new Date(timeOf(it)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} {it.platform ?? ''}{it.status === 'pending' ? ' · needs review' : ''}
                  </span>
                ))}
                {dayItems.length === 0 && <span className="wd-empty">—</span>}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// The draft text + an inline editor. The customer can rewrite any post that isn't live yet
// (pending / approved / scheduled) before it goes out — change wording, fix a typo, retone.
function DraftBody({ it, busy, act }: { it: QueueItem; busy: boolean; act: (fn: () => Promise<unknown>) => () => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(it.draft);
  if (editing) {
    return (
      <div className="ap-edit">
        <textarea className="ap-edit-area" rows={4} value={text} disabled={busy} onChange={(e) => setText(e.target.value)} />
        <div className="ap-actions">
          <button className="btn btn-sm btn-primary" disabled={busy || !text.trim()} onClick={() => { act(() => editItem(it.id, text.trim()))(); setEditing(false); }}>Save</button>
          <button className="btn btn-sm" disabled={busy} onClick={() => { setText(it.draft); setEditing(false); }}>Cancel</button>
        </div>
      </div>
    );
  }
  return (
    <div className="ap-draft-wrap">
      <p className="ap-draft">{it.draft}</p>
      <button className="link edit-link" disabled={busy} onClick={() => { setText(it.draft); setEditing(true); }}>Edit</button>
    </div>
  );
}

// Polls /autopilot/post-result every 10s for a dispatching item and calls onDone when posted.
function PostingPoller({ it, onDone }: { it: QueueItem; onDone: () => void }) {
  const [msg, setMsg] = useState('Waiting for GitHub Actions…');
  useEffect(() => {
    if (!it.dispatch_id) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await pollPostResult(it.dispatch_id!);
        if (cancelled) return;
        if (r.status === 'posted') { onDone(); return; }
        if (r.status === 'failed') { setMsg(`Failed: ${(r as any).error ?? 'unknown'}`); return; }
        setMsg('Still running…');
        setTimeout(poll, 10_000);
      } catch { if (!cancelled) setTimeout(poll, 10_000); }
    };
    const t = setTimeout(poll, 5_000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [it.dispatch_id, onDone]);
  return <p className="ap-draft muted">{msg}</p>;
}

// Platforms where video upload has no open API — the app still ACCEPTS the attempt: it hands the
// customer their clip + caption and opens the platform's own upload page (their device → platform,
// zero Oriora bytes). Never a dead-end, never a silent drop.
const MANUAL_UPLOAD_PLATFORMS: { id: string; name: string; uploadUrl: string }[] = [
  { id: 'youtube', name: 'YouTube (manual upload)', uploadUrl: 'https://www.youtube.com/upload' },
  { id: 'instagram', name: 'Instagram (manual upload)', uploadUrl: 'https://www.instagram.com/' },
  { id: 'tiktok', name: 'TikTok (manual upload)', uploadUrl: 'https://www.tiktok.com/tiktokstudio/upload' },
];

function ApprovedCard({ it, busy, act, mediaMap, mediaFiles, onReload }: {
  it: QueueItem; busy: boolean;
  act: (fn: () => Promise<unknown>) => () => void;
  mediaMap: Record<string, string>;
  mediaFiles: MediaFile[];
  onReload: () => void;
}) {
  const [p, setP] = useState(WIRED_PLATFORMS[0]);
  const [when, setWhen] = useState('');
  const [attaching, setAttaching] = useState(false);
  const [attachErr, setAttachErr] = useState('');

  const hasMedia = !!it.media_path;
  const previewUrl = hasMedia ? mediaMap[it.media_path!] : '';
  const useMediaRoute = hasMedia && MEDIA_PLATFORMS.includes(p);
  const manual = MANUAL_UPLOAD_PLATFORMS.find((m) => m.id === p);
  // Media attached but the chosen API platform can't carry it → the text would post and the clip
  // would silently vanish. Say so BEFORE the click, never after.
  const mediaWouldDrop = hasMedia && !manual && !useMediaRoute;

  function openExternalUrl(url: string) {
    if (isTauri) void invoke('open_external', { url });
    else window.open(url, '_blank');
  }

  async function doPost() {
    if (useMediaRoute) {
      act(() => postWithMedia(it.id, it.media_path!, p).then(() => {}))();
    } else {
      act(() => postItem(it.id, p))();
    }
  }

  async function doAttach(path: string) {
    setAttaching(true); setAttachErr('');
    try { await attachMedia(it.id, path); onReload(); }
    catch (e) { setAttachErr(String(e)); }
    finally { setAttaching(false); }
  }
  async function doDetach() {
    setAttaching(true); setAttachErr('');
    try { await detachMedia(it.id); onReload(); }
    catch (e) { setAttachErr(String(e)); }
    finally { setAttaching(false); }
  }

  return (
    <div className="ap-card">
      <div className="ap-meta"><span className="badge approved">approved</span></div>
      <DraftBody it={it} busy={busy} act={act} />

      {/* Media attachment strip */}
      {hasMedia ? (
        <div className="ap-media">
          <span className="media-badge">📎 {it.media_path!.replace('media/', '')}</span>
          {previewUrl && <a className="link" href={previewUrl} target="_blank" rel="noreferrer">Preview ↗</a>}
          <button className="link dim" disabled={attaching || busy} onClick={doDetach}>Remove</button>
        </div>
      ) : mediaFiles.length > 0 ? (
        <div className="ap-media">
          <select disabled={attaching || busy} defaultValue="" onChange={(e) => { if (e.target.value) doAttach(e.target.value); }}>
            <option value="">Attach media…</option>
            {mediaFiles.map((f) => <option key={f.path} value={f.path}>{f.name}</option>)}
          </select>
        </div>
      ) : null}
      {attachErr && <p className="error">{attachErr}</p>}

      <div className="ap-actions">
        <select value={p} disabled={busy} onChange={(e) => setP(e.target.value)}>
          {WIRED_PLATFORMS.map((x) => <option key={x} value={x}>{x}</option>)}
          {hasMedia && MANUAL_UPLOAD_PLATFORMS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        {manual ? (
          <>
            <button className="btn btn-sm btn-primary" disabled={!previewUrl}
              onClick={() => { navigator.clipboard.writeText(it.draft); if (previewUrl) openExternalUrl(previewUrl); openExternalUrl(manual.uploadUrl); }}>
              Copy caption &amp; open {manual.name.replace(' (manual upload)', '')} ↗
            </button>
          </>
        ) : (
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={doPost}>
            {useMediaRoute ? 'Post with media' : hasMedia ? 'Post text only' : 'Post now'}
          </button>
        )}
        {!manual && (
          <>
            <input className="dt" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
            <button className="btn btn-sm" disabled={busy || !when} onClick={act(() => schedule(it.id, p, new Date(when).toISOString()))}>Schedule</button>
          </>
        )}
        <button className="btn btn-sm" onClick={() => navigator.clipboard.writeText(it.draft)}>Copy</button>
      </div>
      {mediaWouldDrop && (
        <p className="muted" style={{ fontSize: 11, color: '#d97706', margin: '4px 0 0' }}>
          📎 The attached clip rides along only on X for now — on {p} this posts the text alone. Pick a manual-upload platform to post the clip yourself.
        </p>
      )}
      {manual && (
        <p className="muted" style={{ fontSize: 11, margin: '4px 0 0' }}>
          Direct upload to {manual.name.replace(' (manual upload)', '')} isn&apos;t wired yet — this copies your caption, opens the clip (save it), and opens the upload page. Your file goes straight from your device to the platform.
        </p>
      )}
    </div>
  );
}
